import type { DataGridProProps, GridGroupNode } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataGridApiRefContext } from '@/shared/ui';

interface TreeDataGridRowsExpansionRestoreProps {
  isGroupExpandedByDefault: DataGridProProps['isGroupExpandedByDefault'];
}

export const useTreeDataGridRowsExpansionRestoreProps =
  (): TreeDataGridRowsExpansionRestoreProps => {
    const { apiRef } = useDataGridApiRefContext();
    const [pathsExpandedNodes, setPathsExpandedNodes] = useState<string[][]>(
      [],
    );
    const pathsRef = useRef(pathsExpandedNodes);
    // HACK have to wrap handler in callback to avoid bugs in grid (ref used only to access latest value in pathsExpandedNodes)
    // (if remove useCallback fetch for group data fails and appends group items to global list instead of parent children)
    const isGroupExpandedByDefault = useCallback<
      Exclude<DataGridProProps['isGroupExpandedByDefault'], undefined>
    >(
      (node) => {
        const nodePath = getNodePath(node);

        return pathsRef.current.some((path) => {
          return isSamePath(path, nodePath);
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    useEffect(() => {
      pathsRef.current = pathsExpandedNodes;
    }, [pathsExpandedNodes]);

    useEffect(() => {
      if (!apiRef) {
        return;
      }

      const unsubscribe = apiRef.subscribeEvent(
        'rowExpansionChange',
        (node) => {
          const nodePath = getNodePath(node);

          if (node.childrenExpanded) {
            setPathsExpandedNodes((oldPathsExpandedNodes) => {
              return [...oldPathsExpandedNodes, nodePath];
            });
          } else {
            setPathsExpandedNodes((oldPathsExpandedNodes) => {
              return oldPathsExpandedNodes.filter((path) => {
                return !isSamePath(nodePath, path);
              });
            });
          }
        },
      );

      return () => {
        unsubscribe();
      };
    }, [apiRef]);

    return {
      isGroupExpandedByDefault,
    };
  };

const getNodePath = (node: GridGroupNode): string[] => {
  return (node as unknown as { path: string[] }).path;
};

const isSamePath = (pathA: string[], pathB: string[]) => {
  return (
    pathA.length === pathB.length &&
    pathA.every((part, ind) => {
      return part === pathB[ind];
    })
  );
};
